import {
  Box,
  Button,
  Center,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { TextField } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";

export const TagEditModal = observer(function TagEditModal() {
  const { tagStore, tagModalStore } = useStore();

  return (
    <Modal
      autoFocus
      isOpen={true}
      onClose={tagModalStore.modalState.onClose}
      scrollBehavior="inside"
      size="2xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Edit Tags</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" maxH="full">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>
                  <Trans>Title</Trans>
                </Th>
                <Th w="100px">
                  <Trans>Actions</Trans>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {tagModalStore.tagRows.length > 0 ? (
                tagModalStore.tagRows.map(({ id, field }) => {
                  const isUpdating = tagStore.updatingTagIds.has(id);
                  const isEditing = tagModalStore.editingTagIds.has(id);

                  return (
                    <Tr key={id}>
                      <Td>
                        {isEditing ? (
                          <TextField
                            value={field.value}
                            error={field.error}
                            onChange={field.onChange}
                          />
                        ) : (
                          <Text>{field.value}</Text>
                        )}
                      </Td>
                      {isEditing ? (
                        <Td>
                          <HStack spacing="2">
                            <IconButton
                              aria-label={t`Confirm edit tag`}
                              colorScheme="green"
                              icon={<Icon name="check" />}
                              isLoading={isUpdating}
                              onClick={async () => {
                                const validation = await field.validate();
                                if (validation.hasError) return;
                                tagModalStore.handleConfirmEditTag(
                                  id,
                                  validation.value,
                                );
                              }}
                              size="sm"
                              variant="subtle"
                            />
                            <IconButton
                              ml="1"
                              aria-label={t`Cancel edit tag`}
                              colorScheme="red"
                              icon={<Icon name="x-close" />}
                              isLoading={isUpdating}
                              onClick={() => {
                                tagModalStore.handleCancelEditTag(id);
                              }}
                              size="sm"
                              variant="subtle"
                            />
                          </HStack>
                        </Td>
                      ) : (
                        <Td>
                          <HStack spacing="2">
                            <IconButton
                              aria-label={t`Edit tag`}
                              icon={<Icon name="edit-02" />}
                              isLoading={isUpdating}
                              onClick={() => {
                                tagModalStore.handleEditTag(id);
                              }}
                              size="sm"
                              variant="ghost"
                            />
                            <IconButton
                              aria-label={t`Delete tag`}
                              colorScheme="red"
                              icon={<Icon name="trash-03" />}
                              isLoading={isUpdating}
                              onClick={() => {
                                tagModalStore.handleDeleteTag(id);
                              }}
                              size="sm"
                              variant="ghost"
                            />
                          </HStack>
                        </Td>
                      )}
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={2}>
                    <Text color="gray.600" textAlign="center">
                      <Trans>No tags yet. Create one below.</Trans>
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <HStack align="flex-start" justify="flex-end" w="full" spacing="2">
            <Box>
              <TextField
                onChange={tagModalStore.newTagField.onChange}
                value={tagModalStore.newTagField.value}
                placeholder={t`Enter new tag`}
                error={tagModalStore.newTagField.error}
              />
            </Box>
            <Center minH="9">
              <Button
                isLoading={tagStore.isCreating}
                leftIcon={<Icon name="plus" />}
                onClick={tagModalStore.handleCreateTag}
                variant="link"
              >
                <Trans>Add new tag</Trans>
              </Button>
            </Center>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
